import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { YStack, XStack, Text, ScrollView, Button, View } from "tamagui";
import { RiUserLine, RiHome4Line } from "react-icons/ri";
import { colors } from "@styles/colors";
import api from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { formatDate } from "@utils/func/timeFormatter";
import { isBeforeUseStart } from "@utils/func/couponPeriod";
import { applicableItemsLabel, isApplicableItemsSelectableInApp } from "@utils/func/couponScope";
import { getColor } from "@utils/func/colorTool";
import { IdentityContext, useUser } from "@utils/store/user/useUser";
import { useOrder } from "@utils/store/cart/useOrder";
import { appToast } from "@components/Toast";
import { track } from "@lib/tracker";

import { CouponCard } from "./components/coupons/CouponCard";
import { PageLoading } from "@components/loading/PageLoading";
import { CouponSectionHeader } from "./components/coupons/CouponSectionHeader";
import { AccountGroupLabel } from "./components/coupons/AccountGroupLabel";
import { useSkinV2Page, SkinTabs } from "@screen/Home/pages/GasShop/skin";
import { Coupon, Account } from "./components/coupons/couponData";

interface CouponItem {
  customer_coupon_id: string;
  coupon_id: string;
  coupon_name: string;
  coupon_code: string;
  coupon_type: string;
  discount_value: number;
  min_order_amount: number;
  applicable_items: string;
  supplier_id: string | null;
  status: "available" | "used" | "expired";
  received_method: string;
  received_time_stamp: string;
  use_start_time: string | null;
  expire_time_stamp: string;
  used_time_stamp: string | null;
  used_platform_order_id: string | null;
}

const Coupons = () => {
  const skinV2 = useSkinV2Page();
  const [tab, setTab] = useState<"available" | "used" | "expired">("available");
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const scrollRef = useRef<ScrollView>(null);
  const { cusJwtToken } = useCusAuth();
  // 換頁只用頁內 loading，不拉全域遮罩（見 components/loading/PageLoading.tsx）
  const [loading, setLoading] = useState(false);
  // 從帳戶(context)頁進入時帶 context：只顯示平台券 + 該帳戶 supplier 的帳戶券。
  const location = useLocation();
  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const supplierScope = ctx?.primary_supplier?.supplier_id ?? null;

  const navigate = useNavigate();
  const { chooseIdentity, setChooseIdentity } = useUser();
  const { setPendingCouponId } = useOrder();

  // 全部 context 清單：用來 (1) 帳戶券群組標出帳戶名 (2) 點帳戶券時切到對應帳戶身分
  const [contextList, setContextList] = useState<IdentityContext[]>([]);
  useEffect(() => {
    if (!cusJwtToken) return;
    let active = true;
    api
      .get("/app-api/customer/contexts", { headers: { Authorization: `Bearer ${cusJwtToken}` } })
      .then((res) => {
        if (!active) return;
        setContextList((res.data?.data?.contexts ?? []) as IdentityContext[]);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [cusJwtToken]);

  // supplier_id → 帳戶名（context_name），讓帳戶券群組能標出是「哪個帳戶」而非籠統「帳戶券」
  const supplierNames: Record<string, string> = {};
  contextList.forEach((c) => {
    const sid = c.primary_supplier?.supplier_id;
    if (sid) supplierNames[String(sid)] = c.context_name || "帳戶券";
  });

  // 點券去下單：把券存成 pending（購物車載入後套用），帳戶券再切到對應帳戶身分，最後跳訂購頁。
  const useCouponToOrder = (coupon: Coupon) => {
    // 未到使用開始時間的券不可拿去下單（購物車會直接套用、繞過選券守門，故在源頭擋）
    if (isBeforeUseStart(coupon.useStart)) {
      track("coupon_use_click", { blocked_reason: "before_use_start" });
      appToast.error(`此優惠券 ${coupon.useStart ? formatDate(coupon.useStart) : ""} 起才可使用`);
      return;
    }
    // 僅配件券無法用於瓦斯訂單（App 購物車只有瓦斯）
    if (!isApplicableItemsSelectableInApp(coupon.applicableItems)) {
      track("coupon_use_click", { blocked_reason: "not_applicable_gas_only" });
      appToast.error("此優惠券僅限配件使用，無法用於瓦斯訂單");
      return;
    }
    track("coupon_use_click", {});
    setPendingCouponId(coupon.id);
    if (coupon.supplierId) {
      const target = contextList.find((c) => String(c.primary_supplier?.supplier_id) === String(coupon.supplierId));
      if (target && target.context_id !== chooseIdentity?.context_id) setChooseIdentity(target);
    }
    appToast.success("已選優惠券，結帳時自動套用");
    track("entry_click", { entry_source: "coupon" });
    navigate("/gasShop/products");
  };

  const fetchCoupons = async (status: string) => {
    if (!cusJwtToken) return;
    setLoading(true);
    try {
      const res = await api.get("/app-api/coupons", {
        params: {
          status,
          limit: 100,
          page: 1,
        },
        headers: { Authorization: `Bearer ${cusJwtToken}` },
      });

      const items: CouponItem[] = res.data.data.items;
      const mappedCoupons: Coupon[] = items.map((item) => ({
        id: item.customer_coupon_id,
        title: item.coupon_name,
        amount: item.discount_value,
        minSpend: item.min_order_amount,
        expiry: item.expire_time_stamp ? formatDate(item.expire_time_stamp) : "",
        scope: applicableItemsLabel(item.applicable_items),
        source: item.supplier_id ? "帳戶券" : "平台券",
        applicableItems: item.applicable_items,
        used: item.status === "used",
        usedDate: item.used_time_stamp ? formatDate(item.used_time_stamp) : null,
        supplierId: item.supplier_id,
        code: item.coupon_code,
        useStart: item.use_start_time,
      }));

      setCoupons(mappedCoupons);
    } catch (error) {
      setCoupons([]);
      console.error("fetchCoupons error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons(tab);
    scrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [tab, cusJwtToken]);

  // context 範圍：平台券（無 supplier）+ 該帳戶 supplier 的帳戶券；無 context 則顯示全部。
  const scopedCoupons = supplierScope
    ? coupons.filter((c) => !c.supplierId || c.supplierId === supplierScope)
    : coupons;
  const personalCoupons = scopedCoupons.filter((c) => !c.supplierId);
  const accountCoupons = scopedCoupons.filter((c) => c.supplierId);

  // Group account coupons
  const accountCouponsBySupplier: Record<string, { label: string; items: Coupon[] }> = {};
  accountCoupons.forEach((c) => {
    if (c.supplierId) {
      const supplierId = c.supplierId;
      if (!accountCouponsBySupplier[supplierId]) {
        accountCouponsBySupplier[supplierId] = {
          label: supplierNames[supplierId] || "帳戶券",
          items: [],
        };
      }
      accountCouponsBySupplier[supplierId].items.push(c);
    }
  });

  return (
    <ScrollView ref={scrollRef} showsVerticalScrollIndicator={false}>
      {/* 分頁籤與訂單列表同一款（SkinTabs tone="ink"）：深字＋藍底線，直接坐在淡霧頁首上。
          原本是一條白底膠囊分段器——它自帶不透明底，會把整頁背景橫切一段，
          而且同一個 App 裡出現兩種分頁形狀（這裡膠囊、訂單頁底線）會讀成兩套東西。 */}
      {/* V2 不釘住分頁籤：它現在沒有自己的底色（要讓漸層透出來），釘住的話捲動時
          內容會直接從字底下穿過去；而且 top: 0 是內容區的頂端，會鑽到釘住的標題列底下。
          訂單列表的分頁籤同樣是跟著捲走的。V1 維持原本的 sticky 膠囊（它有不透明底）。 */}
      <YStack style={skinV2 ? undefined : { position: "sticky", top: 0, zIndex: 10 }} pt="$1">
        {skinV2 ? (
          <XStack pb={2}>
            <SkinTabs
              tone="ink"
              value={tab}
              onChange={(v) => setTab(v as typeof tab)}
              tabs={[
                { key: "available", label: "未使用" },
                { key: "used", label: "已使用" },
                { key: "expired", label: "已過期" },
              ]}
            />
          </XStack>
        ) : (
          <XStack bg={colors.light} p="$1" br={999} elevation={1}>
            {[
              { id: "available", label: "未使用" },
              { id: "used", label: "已使用" },
              { id: "expired", label: "已過期" },
            ].map((t) => (
              <Button key={t.id} f={1} bg={tab === t.id ? colors.primary : "transparent"} br={999} onPress={() => setTab(t.id as any)} chromeless>
                <Text fontSize={13} fow="700" color={tab === t.id ? colors.light : colors.gray}>
                  {t.label}
                </Text>
              </Button>
            ))}
          </XStack>
        )}
      </YStack>
      {/* 分頁籤與第一個區塊標題（個人券）之間留 24px：分頁籤底線在漸層的收尾段，
          間距太緊會讓「個人券」那列貼著還沒褪完的淺藍。 */}
      <YStack gap="$4" mt={24}>
        <YStack pb="$10">
          {loading && scopedCoupons.length === 0 ? (
            <PageLoading label="載入優惠券" minHeight={220} />
          ) : scopedCoupons.length === 0 ? (
            <View ai="center" py="$10">
              <Text color={colors.gray} fontSize={14}>
                {tab === "available" ? "目前沒有可用的優惠券" : tab === "used" ? "還沒有使用過任何優惠券" : "沒有已過期的優惠券"}
              </Text>
            </View>
          ) : (
            <>
              {/* 個人券 */}
              {personalCoupons.length > 0 && (
                <YStack mb="$5">
                  <CouponSectionHeader
                    icon={RiUserLine}
                    iconColor={colors.purpleDark}
                    iconBg={colors.bgPurple}
                    title="個人券"
                    badge={tab === "available" ? "跟著你走" : undefined}
                    badgeColor={colors.purpleDark}
                    badgeBg={colors.bgPurple}
                    count={personalCoupons.length}
                    hint={tab === "available" ? "平台發給你的券，只有你能用，其他帳戶成員看不到" : undefined}
                  />
                  <YStack gap="$2.5">
                    {personalCoupons.map((c) => (
                      <CouponCard
                        key={c.id}
                        coupon={c}
                        accentColor={colors.purpleDark}
                        secondaryColor={colors.bgPurple}
                        onPress={tab === "available" ? () => useCouponToOrder(c) : undefined}
                      />
                    ))}
                  </YStack>
                </YStack>
              )}

              {/* 帳戶券 */}
              {accountCoupons.length > 0 && (
                <YStack mb="$4">
                  {Object.entries(accountCouponsBySupplier).map(([supplierId, group]) => (
                    <YStack key={supplierId} mb="$4">
                      <AccountGroupLabel acc={{ id: supplierId, label: group.label, color: colors.primary } as Account} count={group.items.length} />
                      <YStack gap="$2.5">
                        {group.items.map((c) => (
                          <CouponCard
                            key={c.id}
                            coupon={c}
                            accentColor={colors.primary}
                            secondaryColor={`${colors.primary}15`}
                            accLabel={group.label}
                            accColor={colors.primary}
                            onPress={tab === "available" ? () => useCouponToOrder(c) : undefined}
                          />
                        ))}
                      </YStack>
                    </YStack>
                  ))}
                </YStack>
              )}
            </>
          )}
        </YStack>
      </YStack>
    </ScrollView>
  );
};

export default Coupons;
